/**
 * MentorBoardConnected — the "Training Mentor" board wired to the live surface,
 * for the Home detail pane and the apps/mentor satellite. Wrapper-as-adapter
 * (Team Access precedent): resolves the unit scope, fetches the roster + each
 * trainee's targets + the published component catalogue + the skill
 * catalogue, MAPS them into the presentational MentorBoard's view-model, and
 * executes the two target writes.
 *
 * Data (unit scope via useUnitScope — a LOCAL pick, then profile unit →
 * managed-units catalogue, so an admin or a multi-unit lead is never
 * dead-ended on a missing profile unit; the session→location link died with
 * session_assign in the cut-over, AllocationBoardConnected precedent):
 *   GET /me/access/overview          → managed_locations (scope catalogue)
 *   GET /locations/{id}/roster → the trainee roster (SAME roster Team
 *                                      Access uses; the subjects of a unit's
 *                                      targets ARE its board members)
 *   GET /users/{id}/skills           → one read per roster member: their fold,
 *                                      each skill carrying its target
 *   GET /skills                      → the catalogue the composer sets a target on
 *   GET /protocols?state=published → the modules a target names, by title
 *   GET /teams?filter=staffed_location = <id> → team staffing the location
 *   GET /teams/{team_id}/skills      → policy-mandated skill coverage
 *
 * `/learning-goals` is gone (410) and with it the unit-wide goal list. A target
 * lives on its SUBJECT's stream, so the roster is read member by member — there
 * is no unit-scoped targets route to collapse that into, and inventing a
 * client-side one would be a second home for the fact.
 *
 * Target writes are SERVER-AUTHORITATIVE: set and drop call the route and the
 * hook invalidates. Nothing is optimistically flipped, and nothing writes
 * `state` — it is derived by the fold from the records behind the skill, which
 * is why this board has a Drop and no "mark achieved".
 *
 * Chat side-channel: a plain conversational session (the mentor agent's
 * manifest has no command post-processor — no proposal cards to fold, unlike
 * Team Access's access_commands). The agent's tools (set_target, …) execute
 * directly server-side; the board just refreshes after the turn. The composer
 * works session-less too — its first send mints the session via
 * useDeferredFirstSend, then onSessionCreated lets the caller transition into
 * the real conversation.
 */

import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery, useQueryClient } from '@tanstack/react-query'

import { useAuthStore } from '@/stores/auth'
import { useStreamingMessage } from '@/hooks/useStreamingChat'
import { useDeferredFirstSend } from '@/hooks/useDeferredFirstSend'
import { sessionKeys } from '@/hooks/useSessions'
import { BoardAgentChat, ChatInput } from '@/components/chat'
import { Skeleton } from '@/components/ui/skeleton'
import { QuizCatalogueSection } from '@/components/training'
import { AGENT_SLUG } from '../../agent'
import { mainAppUrl } from '@/api/mainAppUrl'
import {
  accessKeys,
  isUnitAuthorityDenied,
  useUnitAccess,
  useUnitScope,
} from '@/hooks/useAccess'
import { listTeams } from '@/api/endpoints/teams'
import { NoUnitAuthority, UnitChooser } from '@/components/access/UnitScopePanels'
import { trainingKeys, useTrainingComponents } from '@/hooks/useTraining'
import {
  sortTargets,
  useSkillCatalogue,
  useTargetMutations,
  useTraineeTargets,
} from '@/hooks/useSkills'
import { catalogueTitleLookup } from '@/lib/skillTitle'
import { todayCivilDate } from '@/utils/dates'
import { randomUUID } from '@/lib/uuid'
import type { Target } from '@/api/endpoints/skills'
import type { SessionResponse } from '@/api/types_session'
import type { SessionDetail } from '@/hooks/useSessions'

import { MentorBoard, type MentorUnitOption } from './MentorBoard'
import { SkillCoverageSection } from './SkillCoverageSection'
import type { TargetDraft, TraineeProgress } from './types'

interface MentorBoardConnectedProps {
  /** Present on the Session page / detail pane; absent on the session-less Home
   *  face (board-only + a session-less composer, see useDeferredFirstSend). */
  session?: SessionDetail
  /** Face mode only. Mentor has no patient frame (team scope), so this is always
   *  undefined in practice — present for AgentDetailViewProps parity. */
  facePatientId?: string | null
  /** Face mode only — fires once the composer's first send mints the session. */
  onSessionCreated?: (session: SessionResponse) => void
}

export function MentorBoardConnected({ session, onSessionCreated }: MentorBoardConnectedProps) {
  const { t, i18n } = useTranslation()
  const queryClient = useQueryClient()
  const me = useAuthStore((s) => s.user)

  // Only the author gets the chooser/switcher (AllocationBoardConnected
  // precedent); a session-less board is the caller's own surface.
  const isAuthor = session ? session.session.created_by.performer === me?.id : true

  const [localUnitId, setLocalUnitId] = useState<string | undefined>()
  const scope = useUnitScope(localUnitId ?? undefined)
  const locationId = scope.status === 'ready' ? scope.unit.id : undefined


  // A location is a place and holds no people, so the denominator of a coverage
  // number is the team that staffs it.
  const teamsQuery = useQuery({
    queryKey: ['teams', { filter: `staffed_location = ${locationId}` }],
    queryFn: () => listTeams({ filter: `staffed_location = ${locationId}` }),
    enabled: !!locationId,
  })
  const teamId = teamsQuery.data?.[0]?.id

  const board = useUnitAccess(locationId)
  const componentsQuery = useTrainingComponents({ status: 'published' })
  const catalogue = useSkillCatalogue()

  // The roster IS the set of subjects whose targets this board shows, so the
  // fan-out is driven by it and starts empty until the roster lands.
  const rosterIds = useMemo(
    () => (board.data?.members ?? []).map((m) => m.user_id),
    [board.data],
  )
  // The catalogue names each target. The fold names a skill by id and carries
  // no title, because a title has a locale and the reader picks it — without
  // this a lead's board reads `advanced-life-support` where `Erweiterte
  // Reanimation` belongs. The catalogue is already loaded above for the target
  // composer, so this costs no extra request.
  const skillTitle = useMemo(
    () => catalogueTitleLookup(catalogue.data?.items ?? [], i18n.language),
    [catalogue.data, i18n.language],
  )
  const traineeTargets = useTraineeTargets(rosterIds, skillTitle)

  const pickUnit = (locId: string) => setLocalUnitId(locId)

  // --- Target writes (server-authoritative; the hook invalidates on success) ---
  const targets = useTargetMutations()
  const [droppingTargetIds, setDroppingTargetIds] = useState<string[]>([])

  const onCreateTarget = (draft: TargetDraft) => {
    targets.set.mutate({
      userId: draft.subjectUserId,
      skillKey: draft.skillKey,
      request: { due_date: draft.dueDate },
      // Minted here and held in `variables`, so TanStack's retry of the same
      // attempt appends the target record once rather than twice.
      idempotencyKey: randomUUID(),
    })
  }

  // Drop takes the TARGET's own version as If-Match, not the skill's: a
  // colleague endorsing the same skill in between moves the skill's version and
  // must not 412 a drop that has nothing to do with it.
  const onDropTarget = (userId: string, target: Target) => {
    setDroppingTargetIds((ids) => [...ids, target.id])
    targets.drop.mutate(
      {
        userId,
        skillKey: target.skill_key,
        targetId: target.id,
        version: target.version,
        idempotencyKey: randomUUID(),
      },
      { onSettled: () => setDroppingTargetIds((ids) => ids.filter((id) => id !== target.id)) },
    )
  }

  // --- View-model assembly: fold targets onto the roster members ---
  //
  // Not memoized. `useTraineeTargets` builds `byUser` fresh on every render, so
  // a memo keyed on it would recompute every time anyway while claiming not to,
  // and `boardVM` is read by exactly one child that is not itself memoized —
  // its identity buys nothing. The work is a map over the roster with a sort
  // per member.
  const boardVM = (() => {
    if (!board.data || scope.status !== 'ready') return null
    // The unit's label comes from the scope that resolved the unit, not from
    // the access read — one fact, one source.
    const unitLabel =
      scope.unit.label && scope.unit.label.trim() !== '' ? scope.unit.label : scope.unit.id

    const trainees: TraineeProgress[] = board.data.members.map((m) => {
      const memberTargets = [...(traineeTargets.byUser.get(m.user_id) ?? [])].sort(sortTargets)
      return {
        user: { id: m.user_id, name: m.name ?? m.user_id },
        targets: memberTargets,
      }
    })

    return { unit: { label: unitLabel }, trainees }
  })()

  const unitOptions: MentorUnitOption[] = scope.status === 'ready' || scope.status === 'choose'
    ? scope.options.map((o) => ({ id: o.id, label: o.label && o.label.trim() !== '' ? o.label : o.id }))
    : []

  const progressLoading = traineeTargets.isLoading
  const progressError = traineeTargets.isError
  const retryProgress = () => traineeTargets.refetch()

  // --- Chat side-channel (plain conversational session; no proposal folding) ---
  const streaming = useStreamingMessage(session?.session.id ?? '')
  const deferred = useDeferredFirstSend({
    sessionId: session?.session.id,
    agentSlug: AGENT_SLUG,
    sendMessage: streaming.sendMessage,
    onCreated: (newSession) => {
      onSessionCreated?.(newSession)
    },
  })

  // A finished turn may have set a target or generated a quiz via the agent's
  // tools (executed server-side) — refresh the targets/roster + the
  // session's messages once streaming ends.
  const wasStreaming = useRef(false)
  useEffect(() => {
    if (streaming.isStreaming) {
      wasStreaming.current = true
    } else if (wasStreaming.current) {
      wasStreaming.current = false
      // The trainee folds only. The skill CATALOGUE is knowledge — a file in
      // the customer's repository — and no turn of a conversation changes it.
      void queryClient.invalidateQueries({ queryKey: ['skills', 'user'] })
      // A generated quiz is a new protocol: the quiz shelf re-reads it.
      void queryClient.invalidateQueries({ queryKey: trainingKeys.components() })
      void queryClient.invalidateQueries({ queryKey: accessKeys.unit(locationId ?? '') })
      if (session?.session.id) {
        void queryClient.invalidateQueries({ queryKey: sessionKeys.detail(session.session.id) })
      }
    }
  }, [streaming.isStreaming, locationId, session?.session.id, queryClient])

  return (
    <div className="flex h-full flex-col bg-white">
      <div className="min-h-0 flex-1 overflow-auto">
        {scope.status === 'loading' ? (
          <BoardSkeleton />
        ) : scope.status === 'error' ? (
          <LoadRetry message={t('mentor.board.loadError')} onRetry={scope.retry} />
        ) : scope.status === 'none' || scope.status === 'choose' ? (
          !isAuthor ? (
            <div className="grid h-full place-items-center p-8 text-center">
              <p className="text-[15px] text-neutral-500">{t('mentor.board.notFramed')}</p>
            </div>
          ) : scope.status === 'none' ? (
            <NoUnitAuthority onOpenProfile={() => window.location.assign(mainAppUrl('/profile'))} />
          ) : (
            <UnitChooser
              options={scope.options}
              onPick={pickUnit}
            />
          )
        ) : boardVM ? (
          <>
            <MentorBoard
              unit={boardVM.unit}
              trainees={boardVM.trainees}
              components={componentsQuery.data ?? []}
              skills={catalogue.data?.items ?? []}
              skillsLoading={catalogue.isLoading}
              skillsError={catalogue.isError}
              onRetrySkills={() => void catalogue.refetch()}
              today={todayCivilDate()}
              progressLoading={progressLoading}
              progressError={progressError}
              onRetryProgress={retryProgress}
              onCreateTarget={onCreateTarget}
              creating={targets.set.isPending}
              onDropTarget={onDropTarget}
              droppingTargetIds={droppingTargetIds}
              unitOptions={unitOptions}
              activeUnitId={locationId}
              onSwitchUnit={isAuthor && unitOptions.length > 1 ? pickUnit : undefined}
              quizCatalogue={<QuizCatalogueSection />}
            />
            {teamId && (
              <div className="mx-auto w-full max-w-4xl px-4 pb-6">
                <SkillCoverageSection teamId={teamId} />
              </div>
            )}
          </>
        ) : board.isError ? (
          isUnitAuthorityDenied(board.error) ? (
            // The server's own sentence names who can assign her and offers the
            // access request; the generic copy that used to stand here said
            // only that the board is for leads.
            <NoUnitAuthority
              onOpenProfile={() => window.location.assign(mainAppUrl('/profile'))}
              deniedMessage={board.error.message}
            />
          ) : (
            <LoadRetry message={t('mentor.board.loadError')} onRetry={() => void board.refetch()} />
          )
        ) : (
          <BoardSkeleton />
        )}
      </div>

      {session && (
        <BoardAgentChat
          session={session}
          streaming={streaming}
          className="max-h-[35%] shrink-0 overflow-y-auto border-t border-neutral-200 bg-neutral-50 px-4 py-2"
        />
      )}

      {/* The composer only appears once the board is grounded in a unit — an
          ungrounded agent would answer without roster/target context. */}
      {locationId && (session || onSessionCreated) && (
        <div className="shrink-0 border-t border-neutral-200 bg-neutral-50 px-4 py-3 pb-safe">
          <ChatInput
            onSend={(content) => void deferred.send(content)}
            isPending={streaming.isStreaming || deferred.isPending}
            placeholder={t('mentor.board.chatPlaceholder')}
          />
        </div>
      )}
    </div>
  )
}

function BoardSkeleton() {
  return (
    <div className="mx-auto w-full max-w-4xl space-y-3 p-4" aria-busy="true">
      <Skeleton className="h-24 w-full rounded-[18px]" />
      <Skeleton className="h-20 w-full rounded-[18px]" />
      <Skeleton className="h-20 w-full rounded-[18px]" />
    </div>
  )
}

function LoadRetry({ message, onRetry }: { message: string; onRetry: () => void }) {
  const { t } = useTranslation()
  return (
    <div className="p-4">
      <p className="text-[15px] text-neutral-700" role="alert">
        {message}{' '}
        <button
          type="button"
          onClick={onRetry}
          className="font-semibold text-primary underline-offset-2 hover:underline"
        >
          {t('actions.retry', 'Retry')}
        </button>
      </p>
    </div>
  )
}
